/** One app's 사용량 (S7, `/analytics/:appId`): its header and the panel.
 *
 *  The window and platform come from the address, with the app page's own
 *  default (24 hours). The list passes them on explicitly, so a choice made
 *  there is the one that is read here. */

import { FilterBar, ButtonLink, Code, CopyButton, PageHeader, AppIcon } from '@/ui'
import { ExternalLink } from 'lucide-react'
import { AnalyticsPanel, useAnalyticsAppReport } from './AnalyticsPanel'
import { analyticsQuery, DETAIL_DAYS, LIST_DAYS } from './format'
import { usePlatform, useWindowDays } from './params'
import { PlatformPicker } from './pickers'

export default function AnalyticsDetail({ appId }: { appId: string }) {
  const [days, setDays] = useWindowDays(DETAIL_DAYS)
  const [platform, setPlatform] = usePlatform()
  // The same cache entry as the panel's, so the header costs no request and
  // follows the chosen window.
  const rep = useAnalyticsAppReport(appId, days, platform)
  const linked = rep.data?.linked
  const name = linked?.name ?? appId

  return (
    <>
      <PageHeader
        title={
          <span className="flex min-w-0 items-center gap-3">
            <AppIcon src={linked?.icon_url} name={name} size={56} />
            <span className="min-w-0">
              <span className="block truncate">{name}</span>
              <span className="mt-0.5 inline-flex items-center gap-1 text-body font-normal">
                <Code>{appId}</Code>
                <CopyButton text={appId} what="번들 ID" />
              </span>
            </span>
          </span>
        }
        docTitle={name}
        parent={{ to: `/analytics${analyticsQuery(days, platform, LIST_DAYS)}`, label: '사용량' }}
        crumb={name}
        actions={
          linked && (
            <ButtonLink to={`/apps/${linked.id}/keywords`} icon={ExternalLink}>
              ASO 상세
            </ButtonLink>
          )
        }
      >
        <FilterBar>
          <PlatformPicker platform={platform} options={rep.data?.platforms ?? []} onChange={setPlatform} />
        </FilterBar>
      </PageHeader>
      <AnalyticsPanel appId={appId} days={days} onDaysChange={setDays} platform={platform} />
    </>
  )
}
